// src/features/kommunikation/components/CredentialsListModal.tsx
//
// ZUGANGSDATEN AUS DATEV (05.10.2026, Freigabe Tim): Bei Kunden mit Lohn und
// Gehalt und hr:exchange kommen die Zugangsdaten aus DATEV. Die Liste
// kennzeichnet diese Zeilen in der Spalte "DATEV" und zeigt den Stand eines
// E-Mail-Änderungsauftrags an DATEV. Das Passwort ist bei diesen Kunden an die
// IBAN in DATEV gebunden: Beim Anlegen ist es optional, beim Bearbeiten nicht
// änderbar. Eine geänderte E-Mail-Adresse wird beim Speichern an DATEV
// übermittelt (CredentialsService.speichereMitDatev).

import React, { useState, useMemo } from 'react';
import { KeyRound, Search, Plus, X, Save, Pencil, RefreshCw, Inbox, Info, AlertTriangle, XCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../../auth';
import { useCredentials } from '../hooks/useCredentials';
import { CommunicationCredential, EmailAuftragStand } from '../types';
import { kundenSchluessel, mitarbeiterSchluessel } from '../services/CredentialsService';

interface CredentialsListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CredentialFormData {
  beraternummer: string;
  mandantennummer: string;
  personalnummer: string;
  email: string;
  passwort: string;
  anrede: 'Herr' | 'Frau' | 'Divers' | '';
  nachname: string;
}

const EMPTY_FORM: CredentialFormData = {
  beraternummer: '',
  mandantennummer: '',
  personalnummer: '',
  email: '',
  passwort: '',
  anrede: '',
  nachname: ''
};

// ============================================
// Styles (außerhalb der Komponente, damit sie nicht
// bei jedem Re-Render neu erstellt werden)
// ============================================

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontWeight: '600',
  borderBottom: '1px solid #E5E7EB',
  fontSize: '12px',
  color: '#6B7280',
  whiteSpace: 'nowrap'
};

const tdStyle: React.CSSProperties = {
  padding: '10px 16px',
  verticalAlign: 'middle',
  fontSize: '13px',
  color: '#111827'
};

const inlineInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #D1D5DB',
  borderRadius: '8px',
  fontSize: '13px',
  boxSizing: 'border-box'
};

const actionButtonStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px'
};

// ============================================
// Validierung
// ============================================

const validatePassword = (pw: string, optional: boolean): string | null => {
  if (!pw) return optional ? null : 'Passwort ist erforderlich';
  if (!/^\d{4}$/.test(pw)) {
    return 'Passwort muss aus genau 4 Ziffern bestehen';
  }
  return null;
};

const validateEmail = (email: string): string | null => {
  if (!email.trim()) return 'E-Mail ist erforderlich';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Ungültige E-Mail-Adresse';
  return null;
};

// passwortOptional: Bei Kunden mit Zugangsdaten aus DATEV kommt das Passwort
// aus der IBAN in DATEV und muss nicht eingegeben werden.
const validateForm = (form: CredentialFormData, identityRequired: boolean, passwortOptional = false): string | null => {
  if (identityRequired) {
    if (!form.beraternummer.trim()) return 'Beraternummer ist erforderlich';
    if (!form.mandantennummer.trim()) return 'Mandantennummer ist erforderlich';
    if (!form.personalnummer.trim()) return 'Personalnummer ist erforderlich';
  }

  const emailError = validateEmail(form.email);
  if (emailError) return emailError;

  const pwError = validatePassword(form.passwort, passwortOptional);
  if (pwError) return pwError;

  return null;
};

// ============================================
// DATEV-Auftragsstand
// ============================================

const auftragsAnzeige = (auftrag: EmailAuftragStand): { text: string; farbe: string } => {
  if (auftrag.status === 'neu' || auftrag.status === 'in_arbeit') {
    return { text: 'Änderung an DATEV läuft', farbe: '#B45309' };
  }
  if (auftrag.status === 'erledigt') {
    return { text: 'von DATEV übernommen', farbe: '#059669' };
  }
  return { text: 'von DATEV abgelehnt', farbe: '#DC2626' };
};

// ============================================
// Helper-Komponente: FormField
// ============================================

interface FormFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}

const FormField: React.FC<FormFieldProps> = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px', fontWeight: 500 }}>
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '8px 12px',
        border: '1px solid #D1D5DB',
        borderRadius: '8px',
        fontSize: '14px',
        boxSizing: 'border-box'
      }}
    />
  </div>
);

// ============================================
// Hauptkomponente
// ============================================

export const CredentialsListModal: React.FC<CredentialsListModalProps> = ({
  isOpen,
  onClose
}) => {
  const { user } = useAuth();
  const { credentials, loading, error, addCredential, loadCredentials, datevKunden, emailAuftraege } = useCredentials();

  // Suche
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Add-Form
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [addForm, setAddForm] = useState<CredentialFormData>(EMPTY_FORM);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);

  // Edit-Form (Inline)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<CredentialFormData>(EMPTY_FORM);
  const [editError, setEditError] = useState<string | null>(null);

  // Hinweis nach dem Speichern einer Bearbeitung (etwa Übermittlung an DATEV)
  const [editHinweis, setEditHinweis] = useState<string | null>(null);

  // Saving Lock
  const [saving, setSaving] = useState<boolean>(false);

  // Gefilterte Credentials
  const filteredCredentials = useMemo(() => {
    if (!searchTerm.trim()) return credentials;
    const term = searchTerm.toLowerCase().trim();
    return credentials.filter(c =>
      c.beraternummer.toLowerCase().includes(term) ||
      c.mandantennummer.toLowerCase().includes(term) ||
      c.personalnummer.toLowerCase().includes(term) ||
      c.email.toLowerCase().includes(term) ||
      (c.nachname ? c.nachname.toLowerCase().includes(term) : false)
    );
  }, [credentials, searchTerm]);

  if (!isOpen) return null;

  const istDatevKunde = (beraternummer: string, mandantennummer: string): boolean =>
    datevKunden.has(kundenSchluessel(beraternummer.trim(), mandantennummer.trim()));

  const addIstDatev = istDatevKunde(addForm.beraternummer, addForm.mandantennummer);

  // ============================================
  // Add Handler
  // ============================================

  const handleAddSubmit = async () => {
    setAddError(null);
    setAddSuccess(null);

    const validationError = validateForm(addForm, true, addIstDatev);
    if (validationError) {
      setAddError(validationError);
      return;
    }

    if (!user?.email) {
      setAddError('Kein Benutzer-Login verfügbar');
      return;
    }

    setSaving(true);

    try {
      const result = await addCredential({
        beraternummer: addForm.beraternummer.trim(),
        mandantennummer: addForm.mandantennummer.trim(),
        personalnummer: addForm.personalnummer.trim(),
        email: addForm.email.trim(),
        passwort: addForm.passwort.trim(),
        anrede: addForm.anrede || null,
        nachname: addForm.nachname.trim() || null,
        aktiv: true,
        created_by_email: user.email
      });

      if (result.success) {
        setAddSuccess(
          `Credential für Personal-Nr. ${addForm.personalnummer.trim()} gespeichert` +
          (result.hinweis ? `. ${result.hinweis}` : '')
        );
        setAddForm(EMPTY_FORM);
      } else {
        setAddError(result.error || 'Speichern fehlgeschlagen');
      }
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setSaving(false);
    }
  };

  const handleAddReset = () => {
    setAddForm(EMPTY_FORM);
    setAddError(null);
    setAddSuccess(null);
  };

  // ============================================
  // Edit Handler
  // ============================================

  const handleEditStart = (credential: CommunicationCredential) => {
    setEditingId(credential.id);
    setEditForm({
      beraternummer: credential.beraternummer,
      mandantennummer: credential.mandantennummer,
      personalnummer: credential.personalnummer,
      email: credential.email,
      passwort: credential.passwort,
      anrede: credential.anrede || '',
      nachname: credential.nachname || ''
    });
    setEditError(null);
    setEditHinweis(null);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
    setEditError(null);
  };

  const handleEditSubmit = async () => {
    setEditError(null);

    // Identitäts-Felder werden read-only angezeigt, sind also automatisch
    // identisch mit dem Original. Validiert wird nur das, was editierbar ist.
    const editIstDatev = istDatevKunde(editForm.beraternummer, editForm.mandantennummer);
    const validationError = validateForm(editForm, false, editIstDatev);
    if (validationError) {
      setEditError(validationError);
      return;
    }

    if (!user?.email) {
      setEditError('Kein Benutzer-Login verfügbar');
      return;
    }

    setSaving(true);

    try {
      // upsertCredential nutzt onConflict auf
      // (beraternummer, mandantennummer, personalnummer) — daher
      // wird genau dieser Datensatz aktualisiert.
      const result = await addCredential({
        beraternummer: editForm.beraternummer,
        mandantennummer: editForm.mandantennummer,
        personalnummer: editForm.personalnummer,
        email: editForm.email.trim(),
        passwort: editForm.passwort.trim(),
        anrede: editForm.anrede || null,
        nachname: editForm.nachname.trim() || null,
        aktiv: true,
        created_by_email: user.email
      });

      if (result.success) {
        setEditHinweis(result.hinweis
          ? `Personal-Nr. ${editForm.personalnummer}: ${result.hinweis}`
          : null);
        setEditingId(null);
        setEditForm(EMPTY_FORM);
      } else {
        setEditError(result.error || 'Speichern fehlgeschlagen');
      }
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // Modal schließen
  // ============================================

  const handleClose = () => {
    setSearchTerm('');
    setShowAddForm(false);
    setAddForm(EMPTY_FORM);
    setAddError(null);
    setAddSuccess(null);
    setEditingId(null);
    setEditForm(EMPTY_FORM);
    setEditError(null);
    setEditHinweis(null);
    onClose();
  };

  // DATEV-Spalte einer Zeile: Herkunft und Stand eines E-Mail-Auftrags
  const renderDatevZelle = (cred: CommunicationCredential) => {
    if (!istDatevKunde(cred.beraternummer, cred.mandantennummer)) {
      return <span style={{ color: '#9CA3AF' }}>—</span>;
    }
    const auftrag = emailAuftraege.get(mitarbeiterSchluessel(cred.beraternummer, cred.mandantennummer, cred.personalnummer));
    const anzeige = auftrag ? auftragsAnzeige(auftrag) : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{ color: '#6B7280' }}>aus DATEV</span>
        {auftrag && anzeige && (
          <span
            style={{ color: anzeige.farbe, fontSize: '12px', fontWeight: 600 }}
            title={auftrag.fehler || auftrag.email || undefined}
          >
            {anzeige.text}
          </span>
        )}
      </div>
    );
  };

  // ============================================
  // Render
  // ============================================

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '1200px',
          width: '95%',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid #E5E7EB',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '8px',
              backgroundColor: '#0EA5E915', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <KeyRound size={18} color="#0EA5E9" />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>
              Credentials verwalten
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>
              {filteredCredentials.length} von {credentials.length} Credentials
            </div>
            <button
              onClick={handleClose}
              title="Schließen"
              aria-label="Schließen"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={20} color="#6B7280" />
            </button>
          </div>
        </div>

        {/* Globale Fehler aus dem Hook */}
        {error && (
          <div style={{
            marginBottom: '16px',
            padding: '12px 14px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: '8px',
            color: '#991B1B',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <XCircle size={16} color="#DC2626" /> {error}
          </div>
        )}

        {/* Hinweis nach dem Speichern einer Bearbeitung */}
        {editHinweis && (
          <div style={{
            marginBottom: '16px',
            padding: '12px 14px',
            backgroundColor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: '8px',
            color: '#1E40AF',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px'
          }}>
            <Info size={16} color="#1E40AF" style={{ flexShrink: 0, marginTop: '2px' }} /> {editHinweis}
          </div>
        )}

        {/* Suche + Add-Toggle */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 320px' }}>
            <Search size={16} color="#9CA3AF" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Suche (Berater, Mandant, Personal-Nr., E-Mail, Nachname)"
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <button
            onClick={() => {
              setShowAddForm(!showAddForm);
              setAddError(null);
              setAddSuccess(null);
            }}
            style={{
              padding: '10px 16px',
              backgroundColor: showAddForm ? '#6B7280' : '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {showAddForm ? <><X size={16} /> Formular ausblenden</> : <><Plus size={16} /> Neues Credential</>}
          </button>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#F0FDF4',
            border: '1px solid #86EFAC',
            borderRadius: '10px'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '15px', color: '#065F46', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={16} color="#059669" /> Neues Credential anlegen
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <FormField
                label="Beraternummer *"
                value={addForm.beraternummer}
                onChange={(v) => setAddForm({ ...addForm, beraternummer: v })}
              />
              <FormField
                label="Mandantennummer *"
                value={addForm.mandantennummer}
                onChange={(v) => setAddForm({ ...addForm, mandantennummer: v })}
              />
              <FormField
                label="Personalnummer *"
                value={addForm.personalnummer}
                onChange={(v) => setAddForm({ ...addForm, personalnummer: v })}
              />
              <FormField
                label="E-Mail *"
                value={addForm.email}
                onChange={(v) => setAddForm({ ...addForm, email: v })}
                type="email"
              />
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px', fontWeight: 500 }}>
                  Anrede
                </label>
                <select
                  value={addForm.anrede}
                  onChange={(e) => setAddForm({ ...addForm, anrede: e.target.value as CredentialFormData['anrede'] })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">— bitte wählen —</option>
                  <option value="Herr">Herr</option>
                  <option value="Frau">Frau</option>
                  <option value="Divers">Divers</option>
                </select>
              </div>
              <FormField
                label="Nachname"
                value={addForm.nachname}
                onChange={(v) => setAddForm({ ...addForm, nachname: v })}
              />
            </div>

            {/* Hinweis für Kunden mit Zugangsdaten aus DATEV */}
            {addIstDatev && (
              <div style={{
                marginBottom: '12px',
                padding: '10px 12px',
                backgroundColor: '#EFF6FF',
                border: '1px solid #BFDBFE',
                borderRadius: '8px',
                color: '#1E40AF',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <Info size={15} color="#1E40AF" style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>
                  Bei diesem Kunden kommen die Zugangsdaten aus DATEV. Das Passwort wird aus der IBAN in DATEV
                  übernommen und muss nur eingegeben werden, wenn dort keine IBAN hinterlegt ist. Eine neue
                  E-Mail-Adresse wird beim Speichern an DATEV übermittelt.
                </span>
              </div>
            )}

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px', fontWeight: 500 }}>
                {addIstDatev ? 'Passwort (4 Ziffern, nur ohne IBAN in DATEV)' : 'Passwort * (4 Ziffern)'}
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={addForm.passwort}
                onChange={(e) => setAddForm({ ...addForm, passwort: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                placeholder="z. B. 1234"
                style={{
                  width: '120px',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'monospace'
                }}
              />
              <div style={{
                fontSize: '12px',
                color: '#92400E',
                marginTop: '6px',
                padding: '10px 12px',
                backgroundColor: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <AlertTriangle size={15} color="#F59E0B" style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>Es müssen die <strong>letzten 4 Stellen der IBAN des Gehaltskontos</strong> des Mitarbeiters sein. Erlaubt sind ausschließlich 4 Ziffern.</span>
              </div>
            </div>

            {addError && (
              <div style={{
                marginBottom: '12px',
                padding: '10px 12px',
                backgroundColor: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '8px',
                color: '#991B1B',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <XCircle size={16} color="#DC2626" /> {addError}
              </div>
            )}
            {addSuccess && (
              <div style={{
                marginBottom: '12px',
                padding: '10px 12px',
                backgroundColor: '#D1FAE5',
                border: '1px solid #86EFAC',
                borderRadius: '8px',
                color: '#065F46',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <CheckCircle2 size={16} color="#059669" /> {addSuccess}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleAddSubmit}
                disabled={saving}
                style={{
                  padding: '10px 16px',
                  backgroundColor: saving ? '#D1D5DB' : '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {saving ? 'Speichere...' : 'Speichern'}
              </button>
              <button
                onClick={handleAddReset}
                disabled={saving}
                style={{
                  padding: '10px 16px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Zurücksetzen
              </button>
            </div>
          </div>
        )}

        {/* Tabelle */}
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#6B7280' }}>
            <Loader2 size={32} color="#D1D5DB" style={{ margin: '0 auto 10px' }} />
            <div style={{ fontSize: '14px', fontWeight: '500' }}>Laden...</div>
          </div>
        ) : filteredCredentials.length === 0 ? (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            color: '#6B7280',
            backgroundColor: '#F9FAFB',
            borderRadius: '10px',
            fontSize: '14px'
          }}>
            <Inbox size={36} color="#D1D5DB" style={{ margin: '0 auto 10px' }} />
            <div style={{ fontWeight: '500' }}>
              {credentials.length === 0
                ? 'Noch keine Credentials hinterlegt. Lege das erste über "Neues Credential" an.'
                : 'Keine Treffer für die aktuelle Suche.'}
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB' }}>
                  <th style={thStyle}>Berater</th>
                  <th style={thStyle}>Mandant</th>
                  <th style={thStyle}>Personal-Nr.</th>
                  <th style={thStyle}>Anrede</th>
                  <th style={thStyle}>Nachname</th>
                  <th style={thStyle}>E-Mail</th>
                  <th style={thStyle}>Passwort</th>
                  <th style={thStyle}>DATEV</th>
                  <th style={thStyle}>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {filteredCredentials.map((cred) => {
                  const isEditing = editingId === cred.id;
                  const zeileIstDatev = istDatevKunde(cred.beraternummer, cred.mandantennummer);

                  if (isEditing) {
                    return (
                      <React.Fragment key={cred.id}>
                        <tr style={{ backgroundColor: '#FFFBEB', borderBottom: '1px solid #F3F4F6' }}>
                          {/* Identitäts-Felder: read-only */}
                          <td style={{ ...tdStyle, color: '#6B7280' }}>{cred.beraternummer}</td>
                          <td style={{ ...tdStyle, color: '#6B7280' }}>{cred.mandantennummer}</td>
                          <td style={{ ...tdStyle, color: '#6B7280' }}>{cred.personalnummer}</td>
                          {/* Editierbar: Anrede */}
                          <td style={tdStyle}>
                            <select
                              value={editForm.anrede}
                              onChange={(e) => setEditForm({ ...editForm, anrede: e.target.value as CredentialFormData['anrede'] })}
                              style={inlineInputStyle}
                            >
                              <option value="">—</option>
                              <option value="Herr">Herr</option>
                              <option value="Frau">Frau</option>
                              <option value="Divers">Divers</option>
                            </select>
                          </td>
                          {/* Editierbar: Nachname */}
                          <td style={tdStyle}>
                            <input
                              type="text"
                              value={editForm.nachname}
                              onChange={(e) => setEditForm({ ...editForm, nachname: e.target.value })}
                              style={inlineInputStyle}
                            />
                          </td>
                          {/* Editierbar: E-Mail */}
                          <td style={tdStyle}>
                            <input
                              type="email"
                              value={editForm.email}
                              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                              style={inlineInputStyle}
                            />
                          </td>
                          {/* Editierbar: Passwort (bei Zugangsdaten aus DATEV gesperrt) */}
                          <td style={tdStyle}>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={4}
                              value={editForm.passwort}
                              disabled={zeileIstDatev}
                              title={zeileIstDatev ? 'Das Passwort kommt aus der IBAN in DATEV' : undefined}
                              onChange={(e) => setEditForm({ ...editForm, passwort: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                              style={{
                                ...inlineInputStyle,
                                fontFamily: 'monospace',
                                width: '70px',
                                backgroundColor: zeileIstDatev ? '#F3F4F6' : undefined
                              }}
                            />
                          </td>
                          {/* DATEV */}
                          <td style={tdStyle}>{renderDatevZelle(cred)}</td>
                          {/* Aktion */}
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                onClick={handleEditSubmit}
                                disabled={saving}
                                title="Speichern"
                                style={{
                                  ...actionButtonStyle,
                                  backgroundColor: saving ? '#D1D5DB' : '#059669',
                                  color: 'white',
                                  cursor: saving ? 'not-allowed' : 'pointer'
                                }}
                              >
                                <Save size={14} />
                              </button>
                              <button
                                onClick={handleEditCancel}
                                disabled={saving}
                                title="Abbrechen"
                                style={{
                                  ...actionButtonStyle,
                                  backgroundColor: '#F3F4F6',
                                  color: '#374151',
                                  cursor: saving ? 'not-allowed' : 'pointer'
                                }}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {editError && (
                          <tr style={{ backgroundColor: '#FEF2F2' }}>
                            <td colSpan={9} style={{ padding: '10px 16px', color: '#991B1B', fontSize: '13px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <XCircle size={14} color="#DC2626" /> {editError}
                              </span>
                            </td>
                          </tr>
                        )}
                        <tr style={{ backgroundColor: '#FFFBEB' }}>
                          <td colSpan={9} style={{ padding: '4px 16px 10px 16px', fontSize: '11px', color: '#92400E' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: '6px' }}>
                              <Info size={13} color="#92400E" style={{ flexShrink: 0, marginTop: '1px' }} />
                              <span>
                                Berater-, Mandanten- und Personal-Nummer sind nicht editierbar (Identität des Datensatzes).
                                {zeileIstDatev
                                  ? ' Die Zugangsdaten kommen aus DATEV: Das Passwort folgt der IBAN in DATEV, eine geänderte E-Mail-Adresse wird beim Speichern an DATEV übermittelt.'
                                  : ' Passwort: 4 Ziffern, letzte 4 Stellen der IBAN des Gehaltskontos.'}
                              </span>
                            </span>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  }

                  // Normale Anzeige-Zeile
                  return (
                    <tr key={cred.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={tdStyle}>{cred.beraternummer}</td>
                      <td style={tdStyle}>{cred.mandantennummer}</td>
                      <td style={tdStyle}>{cred.personalnummer}</td>
                      <td style={tdStyle}>{cred.anrede || '—'}</td>
                      <td style={tdStyle}>{cred.nachname || '—'}</td>
                      <td style={tdStyle}>{cred.email}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{cred.passwort}</td>
                      <td style={tdStyle}>{renderDatevZelle(cred)}</td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => handleEditStart(cred)}
                          title="Bearbeiten"
                          style={{
                            ...actionButtonStyle,
                            backgroundColor: '#3B82F6',
                            color: 'white'
                          }}
                        >
                          <Pencil size={14} /> Bearbeiten
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', gap: '8px' }}>
          <button
            onClick={loadCredentials}
            disabled={loading}
            style={{
              padding: '10px 16px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <RefreshCw size={16} /> Aktualisieren
          </button>
          <button
            onClick={handleClose}
            style={{
              padding: '10px 16px',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};
